import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import {
  GetLoyaltyEntitlementConsentEntitlementTypeEnum,
  GetLoyaltyEntitlementConsentTypeEnum,
  LoyaltyEntitlementStatus,
  useLoyaltyApi,
} from '@packages/shared/src/hooks/useLoyaltyApi/useLoyaltyApi';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useIntl, defineMessages } from 'react-intl';
import { useSWRConfig } from 'swr';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import useSWRMutation from 'swr/mutation';
import { useState } from 'react';
import { usePaybackClient } from './usePaybackClient';
import { mappedEntitlementToStatus } from '../Loyalty/mappedEntitlementToStatus';
import { LoyaltyEntitlementProvider } from '../Loyalty/LoyaltyEntitlementProvider';
import { PaybackObserverOverlay } from './PaybackObserverOverlay';

const messages = defineMessages({
  CardHeadline: {
    defaultMessage:
      // say you want to add a third
      // defaultMessage: '{entitlementType, select, Marketing {Marketing Einverständnis} Service {{companyName} und PAYBACK Konten-Verknüpfung} points {PAYBACK Punktestand} other {{companyName} PAYBACK}}',
      '{entitlementType, select, marketing {Marketing Einverständnis} other {PAYBACK Services bei {companyName}}}',
    id: 'Payback.Card.Headline',
  },
  ActivatedStatus: {
    defaultMessage: '{entitlementType, select, marketing {Zugestimmt} other {Aktiviert}}',
    id: 'Payback.Status.Activated',
  },
  DeactivatedStatus: {
    defaultMessage: '{entitlementType, select, marketing {Nicht zugestimmt} other {Deaktiviert}}',
    id: 'Payback.Status.Deactivated',
  },
  NoTextFoundLayer: {
    defaultMessage:
      'Es konnten leider keine Inhalte geladen werden. Versuchen Sie es bitte später noch einmal.',
    id: 'Payback.NoTextFound.Layer',
  },
});
const SuccessIcon = () => <span className="icon-emp-checkmark bg-color-success h-3 w-3" />;
const FailIcon = () => <span className="icon-emp-cancel-fat bg-color-error h-3 w-3" />;

type AgreementCardProps = {
  entitlementType: GetLoyaltyEntitlementConsentEntitlementTypeEnum;
  entitlementStatus: LoyaltyEntitlementStatus;
};

export const PaybackAgreementCard = ({
  entitlementType,
  entitlementStatus,
}: AgreementCardProps) => {
  const {
    company: { name: companyName },
  } = useConfig();
  const { mutate } = useSWRConfig();
  const [showOverlay, setShowOverlay] = useState(false);
  const { addNotification } = useNotification();
  const loyaltyApi = useLoyaltyApi();
  const { paybackClient, setDialogState } = usePaybackClient();
  const { loggedIn } = useLoginState();
  const { locale, formatMessage } = useIntl();
  const { trigger, isMutating } = useSWRMutation(
    loyaltyApi && loggedIn ? `get${entitlementType}LoyaltyEntitlementConsent` : null,
    () =>
      loyaltyApi?.getLoyaltyEntitlementConsent({
        ecLocale: locale,
        type: GetLoyaltyEntitlementConsentTypeEnum.Current,
        entitlementType,
      }),
  );

  const buttonHandler = async () => {
    const result = await trigger();
    const displayModal =
      entitlementType === GetLoyaltyEntitlementConsentEntitlementTypeEnum.Service ||
      (entitlementType === GetLoyaltyEntitlementConsentEntitlementTypeEnum.Marketing &&
        result?.status !== LoyaltyEntitlementStatus.Rejected);

    if (displayModal && result) {
      if (!result.text) {
        // fail fast principle
        addNotification({
          severity: 'error',
          message: formatMessage(messages.NoTextFoundLayer),
        });
        return;
      }
      setDialogState({
        dialogTitle: formatMessage(messages.CardHeadline, {
          entitlementType,
          companyName,
        }),
        dialogContent: (
          <div
            className="[&>div>div>p:first-of-type>strong]:text-size-headline-2xl [&>*]:text-size-body-sm [&_h6]:text-size-headline-xl [&_h6]:mt-2 [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: result.text }}
          />
        ),
        additionalDialogAction: (
          <LoyaltyEntitlementProvider
            entitlementEnum={entitlementType}
            shortName={result.shortName}
            status={result.status}
          >
            {mappedEntitlementToStatus[entitlementType][result?.status].ctaNode}
          </LoyaltyEntitlementProvider>
        ),
        open: true,
      });
    }
    if (
      entitlementType === GetLoyaltyEntitlementConsentEntitlementTypeEnum.Marketing &&
      entitlementStatus === LoyaltyEntitlementStatus.Rejected
    )
      // this is a temporary solution until the BE loyalty overlay is implemented
      paybackClient?.createModuleInOverlay(
        {
          moduleType: 'entitlement',
          contextKey: 'overlayContext',
          options: {
            entitlementGroup: 'OIMIKI_MARKETING',
          },
          onSuccess: () => {
            mutate('getLoyaltyAccountData');
          },
        },
        {
          backdropDisabled: true,
          height: 700,
          maximumWidth: 300,
        },
      );
    setShowOverlay(true);
  };

  return (
    <>
      <Card>
        <CardHeader hasDivider>
          <h5>{formatMessage(messages.CardHeadline, { entitlementType, companyName })}</h5>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-1 py-1">
          <div className="flex items-center gap-1">
            {entitlementStatus === LoyaltyEntitlementStatus.Accepted ? (
              <>
                <SuccessIcon />
                <p>
                  {formatMessage(messages.ActivatedStatus, {
                    entitlementType,
                  })}
                </p>
              </>
            ) : (
              <>
                <FailIcon />
                <p>
                  {formatMessage(messages.DeactivatedStatus, {
                    entitlementType,
                  })}
                </p>
              </>
            )}
          </div>
          <Button disabled={isMutating} onClick={buttonHandler} variant="link" size="medium">
            {formatMessage(
              mappedEntitlementToStatus[entitlementType][entitlementStatus].activateDialogText,
            )}
          </Button>
        </CardContent>
      </Card>
      {showOverlay && <PaybackObserverOverlay closeOverlay={() => setShowOverlay(false)} />}
    </>
  );
};
